import React, { useEffect, useState } from 'react'
import styled from '@emotion/styled'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'
import RadioGroup from '@mui/material/RadioGroup'

import { getFoiStepperArray, completedFoiStatuses } from '../../util/foiConsts'
import { logo } from '../../images/images.js'
import { formatFOIForOrderSummary } from '../../util/foiFormatting'
import { getPaymentIntentSecret, getBillById, getBillDownload, postInPersonApplicationFee, getFOIStatus } from '../../dataClient/apiCalls'
import { billTypeMap, getBillStepperArray } from '../../util/billingConsts'
import { buildBillPdfFileName, formatBillFromServer } from '../../util/billFormatting.js'
import { handleDownloadResponse } from '../../util/downloadFile.js'
import { theme } from '../../themes/theme'
import {
  CenteredRowContainerReverse,
  LightBGContainer,
  FlexColumn,
  Logo,
  PageWithTitle,
  Header,
  SpaceBetweenRowContainer
} from '../styles/Containers'
import BodyText, { PageTitle, SmallBodyText } from '../styles/Text'
import { RowFormControl, StyledFormControlLabel, StyledRadio } from '../styles/RadioGroup'
import PaymentForm from '../payments/PaymentForm'
import Loading from '../common/Loading'
import LoadingError from '../common/LoadingError'
import Button, { SecondaryButton } from '../common/Button'
import Link from '../styles/Link'
import Carousel from '../common/Carousel'
import { WarningMessage, ErrorMessage, InfoMessage } from '../common/UserMessage'
import FOIConfirmationPaid from '../foi/FOIConfirmationPaid'
import FOIConfirmationAwaitingPayment from '../foi/FOIConfirmationAwaitingPayment'
import Bill from '../bill/Bill'
import BillConfirmation from '../billConfirmations/BillConfirmation'
import OrderSummary from '../payments/OrderSummary'

const MISSING_PAYMENT_SESSION_MESSAGE = ["We're sorry, we could not restore your payment session."]

const LEAVE_PAGE_WARNING =
  'If you exit this page without completing payment, you will need to start your FOI request over. ' +
  'To edit your request, use the "Review Request" button.'

const SectionContainer = styled(LightBGContainer)`
  padding: 40px;
  height: max-content;
  width: auto;

  overflow: visible;
  min-width: ${props => (props.narrow ? '350px' : 'auto')};

  @media only screen and (max-width: 1000px) {
    padding: 25px;
    width: 90%;
    min-width: auto;
  }
`

const BillContainer = styled(SectionContainer)`
  width: ${props => (props.type === 'bill' ? '60%' : '50%')};
`

const PaymentFormContainer = styled(FlexColumn)`
  width: ${props => (props.type === 'bill' ? '40%' : '50%')};
`

const BackButton = styled(SecondaryButton)`
  gap: 10px;
  padding: 13px 20px;
  white-space: nowrap;

  @media only screen and (max-width: 600px) {
    order: 1;
  }
`

const Red = styled.span`
  color: ${theme.colors.error};
`

function FOIPaymentPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { foiId, billId } = useParams()

  const [clientSecret, setClientSecret] = useState('')
  const [paymentAmount, setPaymentAmount] = useState('0')
  const [foiDetails, setFoiDetails] = useState()
  const [billDetails, setBillDetails] = useState()
  const [paymentMethod, setPaymentMethod] = useState('ONLINE')
  const [display, setDisplay] = useState('carousel')

  const [loading, setLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState()
  const [showCreateRequestLink, setShowCreateRequestLink] = useState(false)
  const [isPaymentBlocked, setIsPaymentBlocked] = useState(false)
  const [paymentBlockedMessages, setPaymentBlockedMessages] = useState(['This bill has already been paid.'])
  const [showDownloadInvoiceButton, setShowDownloadInvoiceButton] = useState(false)
  const [inPersonPaymentLoading, setInPersonPaymentLoading] = useState(false)
  const [inPersonPaymentError, setInPersonPaymentError] = useState()
  const [foiStatusFetched, setFoiStatusFetched] = useState(Boolean(foiId || location.state?.foiPaymentSnapshot))

  function handleBack() {
    if (foiId && foiDetails) {
      const { contactInfo, requestInfo } = location.state || {}
      navigate('/foi/create', { state: { contactInfo, requestInfo } })
    } else if (billId) {
      navigate(`/bill/${billId}/review`)
    } else {
      navigate(-1)
    }
  }

  const [downloadingBill, setDownloadingBill] = useState(false)
  const [downloadError, setDownloadError] = useState(false)

  function handleDownloadInvoice() {
    setDownloadingBill(true)
    setDownloadError(false)

    getBillDownload(billId)
      .then(response => {
        handleDownloadResponse(response, buildBillPdfFileName(billDetails?.secondaryId), 'download')
        setDownloadingBill(false)
      })
      .catch(e => {
        console.error('An error occurred while downloading the bill', billId, e)
        setDownloadingBill(false)
        setDownloadError(true)
      })
  }

  function handlePaymentMethodChange(event) {
    setPaymentMethod(event.target.value)
  }

  function handlePayment() {
    setDisplay('confirmation')
  }

  function handleInPersonPayment() {
    if (!billId) {
      setDisplay('confirmation')
      return
    }

    const foiIdToUse = foiId || foiDetails?.id || billDetails?.foiId

    if (!foiIdToUse) {
      setInPersonPaymentError('Unable to determine FOI ID for payment.')
      return
    }

    setInPersonPaymentLoading(true)
    setInPersonPaymentError(undefined)

    postInPersonApplicationFee(foiIdToUse, billId)
      .then(() => {
        setDisplay('confirmation')
      })
      .catch(e => {
        console.error('Failed to submit in-person payment', e)
        if (e.response?.data?.message) {
          setInPersonPaymentError(e.response.data.message)
        } else {
          setInPersonPaymentError('Failed to submit in-person payment. Please try again.')
        }
      })
      .finally(() => {
        setInPersonPaymentLoading(false)
      })
  }

  useEffect(() => {
    if (!billId) {
      setErrorMessage(['Missing required parameters.'])
      setLoading(false)
      return
    }

    setErrorMessage(undefined)
    setLoading(true)
  }, [billId])

  useEffect(() => {
    if (!billId) {
      return
    }

    let cancelled = false
    const snapshot = location.state?.foiPaymentSnapshot

    getBillById(billId)
      .then(({ data }) => {
        if (cancelled) return

        const billData = data.data
        const formattedBill = formatBillFromServer(data.data)
        setBillDetails(formattedBill)

        const resolvedFoiId = foiId || billData.foiId

        if (!resolvedFoiId) {
          setErrorMessage(['Bill does not have an associated FOI ID.'])
          return
        }

        if (foiId && billData.foiId && billData.foiId !== foiId) {
          setErrorMessage(['The bill does not match this Freedom of Information request.'])
          return
        }

        if (foiId && billData.type !== 'APPLICATION_FEE') {
          navigate(`/bill/${billId}/review`)
          return
        }

        const needsFoiContext = Boolean(foiId)

        if (needsFoiContext && !snapshot && formattedBill.status === 'WAITING' && formattedBill.paymentMethod !== 'IN_PERSON') {
          setErrorMessage(MISSING_PAYMENT_SESSION_MESSAGE)
          setShowCreateRequestLink(true)
          return
        }

        if (snapshot) {
          const foiData = {
            ...snapshot,
            id: resolvedFoiId
          }

          if (completedFoiStatuses.includes(foiData.status)) {
            if (foiId) {
              navigate(`/bill/${billId}/review`)
            } else {
              setPaymentBlockedMessages(['The Freedom of Information request has been completed or closed. No further payment is required.'])
              setIsPaymentBlocked(true)
              setFoiDetails(foiData)
            }
            return
          }

          if (formattedBill.status !== 'WAITING') {
            console.error('Bill not available for payment', billId, formattedBill.status)
            setPaymentBlockedMessages(
              formattedBill.status === 'PAID' ? ['This bill has already been paid.'] : ['This bill is no longer available for payment.']
            )
            setIsPaymentBlocked(true)
            setFoiDetails(foiData)
            return
          }

          if (needsFoiContext && formattedBill.paymentMethod === 'IN_PERSON') {
            setPaymentBlockedMessages(['This bill has been marked for in-person payment.'])
            setIsPaymentBlocked(true)
            setShowDownloadInvoiceButton(true)
            setFoiDetails(foiData)
            return
          }

          setFoiDetails(foiData)
        } else {
          setFoiDetails(undefined)

          if (formattedBill.status !== 'WAITING') {
            console.error('Bill not available for payment', billId, formattedBill.status)
            setPaymentBlockedMessages(
              formattedBill.status === 'PAID' ? ['This bill has already been paid.'] : ['This bill is no longer available for payment.']
            )
            setIsPaymentBlocked(true)
            return
          }

          if (needsFoiContext && formattedBill.paymentMethod === 'IN_PERSON') {
            setPaymentBlockedMessages(['This bill has been marked for in-person payment.'])
            setIsPaymentBlocked(true)
            setShowDownloadInvoiceButton(true)
            return
          }
        }
      })
      .catch(e => {
        if (cancelled) return
        console.error('Failed to retrieve bill details', billId, e)
        if (e.response?.data?.message?.includes('billId not found')) {
          setErrorMessage(['The provided bill does not exist.'])
        } else {
          setErrorMessage(['Failed to load bill details. Please try again.'])
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [billId, foiId, location.state])

  useEffect(() => {
    if (foiStatusFetched || !billDetails?.foiId) return

    let cancelled = false

    getFOIStatus(billDetails.foiId)
      .then(({ data }) => {
        if (cancelled) return
        const foiData = data.data
        if (completedFoiStatuses.includes(foiData.status)) {
          setPaymentBlockedMessages(['The Freedom of Information request has been completed or closed. No further payment is required.'])
          setIsPaymentBlocked(true)
        }
        setFoiStatusFetched(true)
      })
      .catch(e => {
        if (cancelled) return
        console.error('Failed to fetch FOI status', billDetails.foiId, e)
        setFoiStatusFetched(true)
      })

    return () => {
      cancelled = true
    }
  }, [foiStatusFetched, billDetails])

  useEffect(() => {
    if (!billDetails || isPaymentBlocked) return
    if (billDetails.id !== billId) return
    if (billDetails.status !== 'WAITING') return
    if (!foiStatusFetched) return

    const snapshot = location.state?.foiPaymentSnapshot
    if (snapshot && completedFoiStatuses.includes(snapshot.status)) return

    function getPaymentIntent() {
      setLoading(true)
      getPaymentIntentSecret(billId, 'BILL')
        .then(({ data }) => {
          setClientSecret(data.clientSecret)
          setPaymentAmount(data.amount)
          setLoading(false)
        })
        .catch(e => {
          console.error('Failed to fetch payment intent secret', e)
          setErrorMessage(['Error getting payment details.', 'Please refresh the page to try again.'])
          setLoading(false)
        })
    }

    if (!foiId) {
      if (!clientSecret) {
        getPaymentIntent()
      }
    } else if (paymentMethod === 'ONLINE') {
      if (!clientSecret) {
        getPaymentIntent()
      }
    } else if (paymentMethod === 'IN_PERSON') {
      setPaymentAmount(billDetails.amountDue.toFixed(2))
      setClientSecret('')
    }
  }, [billDetails, isPaymentBlocked, foiStatusFetched, paymentMethod, billId, clientSecret, foiId, location.state])

  if (loading) return <Loading />

  if (errorMessage !== undefined) {
    return (
      <LoadingError message={errorMessage}>
        {showCreateRequestLink && (
          <SmallBodyText>
            <Link to="/foi/create">Create a new FOI Request</Link>
          </SmallBodyText>
        )}
      </LoadingError>
    )
  }

  if (isPaymentBlocked) {
    const foiIdToUse = foiId || foiDetails?.id || billDetails?.foiId
    return (
      <LoadingError message={paymentBlockedMessages}>
        {showDownloadInvoiceButton && (
          <>
            <Button onClick={handleDownloadInvoice} loading={downloadingBill}>
              Download Invoice
            </Button>
            {downloadError && <ErrorMessage>There was an error downloading your invoice. Please try again.</ErrorMessage>}
          </>
        )}
        {foiIdToUse && !showDownloadInvoiceButton && (
          <Button>
            <Link to={`/foi/${foiIdToUse}/download`} target="_blank">
              Check FOI Status
            </Link>
          </Button>
        )}
        <SmallBodyText>
          <Link to="/foi/create">Create a new FOI Request</Link>
        </SmallBodyText>
      </LoadingError>
    )
  }

  if (display === 'confirmation') {
    return (
      <>
        {!foiId ? (
          <BillConfirmation secondaryId={billDetails?.secondaryId} />
        ) : paymentMethod === 'ONLINE' ? (
          <FOIConfirmationPaid secondaryId={foiDetails?.secondaryId} billSecondaryId={billDetails?.secondaryId} billId={billId} />
        ) : (
          <FOIConfirmationAwaitingPayment foiSecondaryId={foiDetails?.secondaryId} billSecondaryId={billDetails?.secondaryId} billId={billId} />
        )}
      </>
    )
  }

  if (!billDetails) {
    return <LoadingError message={['Unable to load bill details.']} />
  }

  if (foiId && !foiDetails) {
    return <LoadingError message={['Unable to load request details.']} />
  }

  const pageTitle = foiId ? 'Freedom of Information Request Payment' : 'Bill Payment'
  const stepperSteps = foiId ? getFoiStepperArray(true) : getBillStepperArray()
  const activeStep = foiId ? 3 : 1

  return (
    <PageWithTitle gap="40px">
      <Header>
        <Logo src={logo} />
        <PageTitle>{pageTitle}</PageTitle>
      </Header>
      <FlexColumn width="100%" gap="30px">
        {foiId && (
          <WarningMessage>
            <SmallBodyText>{LEAVE_PAGE_WARNING}</SmallBodyText>
          </WarningMessage>
        )}
        <SpaceBetweenRowContainer gap="50px">
          <BackButton onClick={handleBack}>
            <ArrowBackIosIcon />
            {foiId ? 'Review Request' : 'Review'}
          </BackButton>
          <Carousel steps={stepperSteps} activeStep={activeStep} />
        </SpaceBetweenRowContainer>
        <CenteredRowContainerReverse width="100%" gap="30px">
          <PaymentFormContainer type={foiId ? 'foi' : 'bill'}>
            <SectionContainer>
              <FlexColumn gap="15px" width="100%">
                {foiId ? (
                  <>
                    <RowFormControl>
                      <BodyText>
                        How would you like to complete your payment?<Red> *</Red>
                      </BodyText>
                      <RadioGroup row name="payment-method-radio-group" value={paymentMethod} onChange={handlePaymentMethodChange}>
                        <StyledFormControlLabel value="ONLINE" control={<StyledRadio />} label="Online" />
                        <StyledFormControlLabel value="IN_PERSON" control={<StyledRadio />} label="In Person" />
                      </RadioGroup>
                    </RowFormControl>
                    {paymentMethod === 'ONLINE' && clientSecret && <PaymentForm clientSecret={clientSecret} onSuccess={handlePayment} />}
                    {paymentMethod === 'IN_PERSON' && (
                      <>
                        <SecondaryButton onClick={handleInPersonPayment} loading={inPersonPaymentLoading} disabled={inPersonPaymentLoading}>
                          Confirm & Pay In Person
                        </SecondaryButton>
                        {inPersonPaymentError && <ErrorMessage>{inPersonPaymentError}</ErrorMessage>}
                        <WarningMessage>
                          Your request will not be processed by staff until your payment is completed. In person payment information will be provided
                          on the next page.
                        </WarningMessage>
                      </>
                    )}
                  </>
                ) : (
                  clientSecret && <PaymentForm clientSecret={clientSecret} onSuccess={handlePayment} />
                )}
              </FlexColumn>
            </SectionContainer>
            {billDetails?.type === 'FEE_ESTIMATE' && clientSecret && (
              <FlexColumn>
                <InfoMessage>
                  After payment is completed, the FOI staff will begin retrieving and processing your records. Final payment for actual hours worked
                  will be required before you receive access to the finalized records. This amount may increase or decrease from the amount quoted.
                </InfoMessage>
              </FlexColumn>
            )}
          </PaymentFormContainer>
          <BillContainer type={foiId ? 'foi' : 'bill'}>
            <FlexColumn gap="15px">
              {billDetails.type === 'APPLICATION_FEE' && foiId ? (
                <OrderSummary
                  details={formatFOIForOrderSummary(foiDetails)}
                  displayDetails={true}
                  amount={paymentAmount}
                  title="Freedom of Information Request"
                  subtitle={billTypeMap[billDetails.type]}
                  paymentDueDate={billDetails.paymentDueDate}
                />
              ) : (
                <Bill bill={billDetails} />
              )}
            </FlexColumn>
          </BillContainer>
        </CenteredRowContainerReverse>
      </FlexColumn>
    </PageWithTitle>
  )
}

export default FOIPaymentPage
